import { type OngletCritèresFicheFormationProps } from "./OngletCritèresFicheFormation.interface";
import GraphiqueRépartitionMoyenne from "@/components/GraphiqueRépartitionMoyenne/GraphiqueRépartitionMoyenne";
import TexteTronqué from "@/components/TexteTronqué/TexteTronqué";
import Titre from "@/components/Titre/Titre";
import { i18n } from "@/configuration/i18n/i18n";
import { type ChartColor } from "@codegouvfr/react-dsfr/Chart/chartWrapper";
import { PieChart } from "@codegouvfr/react-dsfr/Chart/PieChart";
import { useEffect } from "react";

const trierTableauParPourcentage = <T extends { pourcentage: number }>(tableau: T[]): T[] => {
  return [...tableau].sort((a, b) => b.pourcentage - a.pourcentage);
};

const OngletCritèresFicheFormation = ({
  moyenneGénérale,
  répartitionParBac,
  descriptifAttendus,
}: OngletCritèresFicheFormationProps) => {
  useEffect(() => {
    type Dsfr = {
      colors?: {
        getColor: (category: string, type: string, name: string | string[], options?: { hover?: boolean }) => string;
        __patched?: boolean;
      };
    };

    interface Couleur {
      defaut: string;
      survol: string;
    }

    const dsfr = (window as { dsfr?: Dsfr }).dsfr;

    if (!dsfr?.colors?.getColor || dsfr.colors.__patched) return () => {};

    const original = dsfr.colors.getColor.bind(dsfr.colors);
    dsfr.colors.__patched = true;

    const couleurs: Record<string, Couleur> = {
      "bac-general": { defaut: "#000091", survol: "#1212ff" },
      "bac-s2tmd": { defaut: "#0063cb", survol: "#3b87ff" },
      "bac-st2s": { defaut: "#518fff", survol: "#98b4ff" },
      "bac-stav": { defaut: "#60e0eb", survol: "#4cb4bd" },
      "bac-std2a": { defaut: "#c7f6fc", survol: "#64ecf8" },
      "bac-sthr": { defaut: "#73e0cf", survol: "#5bb5a7" },
      "bac-sti2d": { defaut: "#6fe49d", survol: "#58b77d" },
      "bac-stl": { defaut: "#297254", survol: "#3ea47a" },
      "bac-stmg": { defaut: "#006A6F", survol: "#009fa7" },
      "bac-professionnel": { defaut: "#ffb7ae", survol: "#ffe0dc" },
      "bac-professionnel-agricole": { defaut: "#a94645", survol: "#d5706f" },
    };

    const palette = Object.values(couleurs);
    const cache = new Map<string, Couleur>();
    let index = 0;

    const normalize = (word: string) =>
      word
        .toLowerCase()
        .normalize("NFD")
        .replaceAll(/[\u0300-\u036F]/gu, "")
        .replaceAll(/[^a-z0-9]+/gu, "-");

    const normalizeName = (name: unknown): string | undefined => {
      if (typeof name === "string") return normalize(name);

      if (Array.isArray(name) && typeof name[0] === "string") {
        return normalize(name[0]);
      }

      return undefined;
    };

    const getFallback = (key: string): Couleur => {
      if (cache.has(key)) return cache.get(key)!;

      const couleur = palette[index % palette.length];
      index++;

      cache.set(key, couleur);
      return couleur;
    };

    dsfr.colors.getColor = (_category, _type, name, options) => {
      const noms = Array.isArray(name) ? name : [name];

      for (const nom of noms) {
        if (typeof nom !== "string") continue;

        const key = normalizeName(nom);
        const couleur = key && couleurs[key];

        if (couleur) {
          return options?.hover ? couleur.survol : couleur.defaut;
        }
      }

      const fallbackKey = normalizeName(noms[0]) ?? "default";
      const couleurDiagramme = getFallback(fallbackKey);

      return options?.hover ? couleurDiagramme.survol : couleurDiagramme.defaut;
    };

    return () => {
      if (dsfr?.colors) {
        dsfr.colors.getColor = original;
        delete dsfr.colors.__patched;
      }
    };
  }, []);

  const nomsBac: string[] = [];
  const labelBac: string[] = [];
  const couleursBac: ChartColor[] = [];
  const pourcentages: number[] = [];

  for (const bac of trierTableauParPourcentage(répartitionParBac)) {
    if (bac.pourcentage !== 0) {
      nomsBac.push(`${bac.nomBac} (${bac.pourcentage}%)`);
      labelBac.push(`${bac.nomBac}`);
      couleursBac.push(`${bac.nomBac.toLocaleLowerCase().trim().replaceAll(" ", "-")}` as ChartColor);
      pourcentages.push(bac.pourcentage);
    }
  }

  return (
    <div>
      {répartitionParBac.length > 0 && (
        <div>
          <div className="*:mb-4">
            <Titre
              niveauDeTitre="h2"
              styleDeTitre="text--lg"
            >
              {i18n.PAGE_FORMATION.RÉPARTITION_PAR_BAC}
            </Titre>
          </div>
          <PieChart
            color={
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              couleursBac
            }
            fill
            name={nomsBac}
            x={labelBac}
            y={pourcentages}
          />
        </div>
      )}

      {moyenneGénérale.centiles.length > 0 && (
        <>
          <hr className="mb-2 mt-8 first:hidden" />
          <div className="*:mb-4">
            <Titre
              niveauDeTitre="h2"
              styleDeTitre="text--lg"
            >
              {i18n.PAGE_FORMATION.MOYENNE_GÉNÉRALE} {moyenneGénérale.nomBac && `(${moyenneGénérale.nomBac})`}
            </Titre>
          </div>
          <GraphiqueRépartitionMoyenne notes={moyenneGénérale.centiles.map((centile) => centile.note)} />
        </>
      )}

      {descriptifAttendus && descriptifAttendus !== "" && (
        <>
          <hr className="mb-2 mt-8 first:hidden" />
          <div className="*:mb-4">
            <Titre
              niveauDeTitre="h2"
              styleDeTitre="text--lg"
            >
              {i18n.PAGE_FORMATION.LES_ATTENDUS}
            </Titre>
          </div>
          <TexteTronqué texte={descriptifAttendus} />
        </>
      )}
    </div>
  );
};

export default OngletCritèresFicheFormation;
